import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface SyncProgress {
  total: number;
  processed: number;
  created: number;
  updated: number;
  deleted: number;
  errors: number;
  contentTypes: Record<string, { total: number; processed: number }>;
  currentContentType?: string;
}

interface SyncJob {
  id: string;
  jobType: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: SyncProgress;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface SyncStatusResponse {
  job?: SyncJob;
  status?: 'never_synced';
  site_sync_status?: string;
}

interface SyncStatusPanelProps {
  siteId: string;
  isOpen: boolean;
  onClose: () => void;
  jobId?: string | null;
}

export function SyncStatusPanel({ siteId, isOpen, onClose, jobId }: SyncStatusPanelProps) {
  const queryClient = useQueryClient();
  const [job, setJob] = useState<SyncJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const invalidatedJobRef = useRef<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Poll for status updates when panel is open
  useEffect(() => {
    if (!isOpen) return;

    const fetchStatus = async () => {
      try {
        const response = await api.get<SyncStatusResponse>(`/api/v1/sites/${siteId}/sync/status`);

        if (response.job) {
          const newJob = response.job;

          // Add log entry if status or progress changed
          if (!job || job.status !== newJob.status) {
            const timestamp = new Date().toLocaleTimeString();
            if (newJob.status === 'pending') {
              addLog(`[${timestamp}] Job queued, waiting for worker...`);
            } else if (newJob.status === 'running') {
              addLog(`[${timestamp}] Sync started`);
            } else if (newJob.status === 'completed') {
              addLog(`[${timestamp}] Sync completed successfully`);
            } else if (newJob.status === 'failed') {
              addLog(`[${timestamp}] Sync failed: ${newJob.errorMessage || 'Unknown error'}`);
            } else if (newJob.status === 'cancelled') {
              addLog(`[${timestamp}] Sync cancelled by user`);
            }
          }

          // Log content type progress
          if (newJob.progress?.currentContentType &&
              (!job?.progress?.currentContentType ||
               job.progress.currentContentType !== newJob.progress.currentContentType)) {
            const timestamp = new Date().toLocaleTimeString();
            addLog(`[${timestamp}] Syncing ${newJob.progress.currentContentType}...`);
          }

          setJob(newJob);
        } else if (response.status === 'never_synced') {
          setJob(null);
          addLog('No sync jobs found for this site');
        }

        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch status');
        setLoading(false);
      }
    };

    // Initial fetch
    fetchStatus();

    // Poll every 1 second while running or pending
    const interval = setInterval(() => {
      if (job?.status === 'running' || job?.status === 'pending' || !job) {
        fetchStatus();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, siteId, job?.status, job?.progress?.currentContentType]);

  // Reset logs when jobId changes (new sync started)
  useEffect(() => {
    if (jobId) {
      setLogs([]);
      invalidatedJobRef.current = null; // Reset so new job can trigger invalidation
      const timestamp = new Date().toLocaleTimeString();
      addLog(`[${timestamp}] New sync job created: ${jobId.slice(0, 8)}...`);
    }
  }, [jobId]);

  // Auto-refresh data grid when sync completes
  useEffect(() => {
    if (job?.status === 'completed' && job.id !== invalidatedJobRef.current) {
      invalidatedJobRef.current = job.id;
      // Invalidate all site-content queries to refresh the data grid
      queryClient.invalidateQueries({ queryKey: ['site-content', siteId] });
      const timestamp = new Date().toLocaleTimeString();
      addLog(`[${timestamp}] Data refreshed`);
    }
  }, [job?.status, job?.id, siteId, queryClient]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, message].slice(-50)); // Keep last 50 logs
  };

  const handleCancelSync = async () => {
    setCancelling(true);
    try {
      const result = await api.post<{ success: boolean; message: string }>(
        `/api/v1/sites/${siteId}/sync/cancel`,
        {}
      );
      const timestamp = new Date().toLocaleTimeString();
      addLog(`[${timestamp}] ${result.message}`);
    } catch (err) {
      const timestamp = new Date().toLocaleTimeString();
      addLog(`[${timestamp}] Cancel failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setCancelling(false);
    }
  };

  if (!isOpen) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDuration = (start: string | null, end: string | null) => {
    if (!start) return '-';
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const durationMs = endTime.getTime() - startTime.getTime();
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Sync Status
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-60px)]">
          {loading && !job ? (
            <div className="flex items-center justify-center py-8">
              <svg className="w-6 h-6 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="ml-2 text-gray-600">Loading status...</span>
            </div>
          ) : error ? (
            <div className="text-red-600 bg-red-50 p-3 rounded">{error}</div>
          ) : job ? (
            <>
              {/* Status Badge & Duration */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusColor(job.status)}`}>
                    {job.status.toUpperCase()}
                  </span>
                  {job.status === 'running' && (
                    <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {/* Cancel button - show when sync is active */}
                  {(job.status === 'running' || job.status === 'pending') && (
                    <button
                      onClick={handleCancelSync}
                      disabled={cancelling}
                      className="px-2 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    >
                      {cancelling ? 'Cancelling...' : 'Cancel Sync'}
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Duration: {formatDuration(job.startedAt, job.completedAt)}
                </div>
              </div>

              {/* Progress Summary */}
              {job.progress && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total processed:</span>
                    <span className="font-medium">{job.progress.processed}</span>
                  </div>
                  {job.progress.currentContentType && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Currently syncing:</span>
                      <span className="font-medium text-blue-600">{job.progress.currentContentType}</span>
                    </div>
                  )}
                  {job.progress.errors > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Errors:</span>
                      <span className="font-medium text-red-600">{job.progress.errors}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Content Type Breakdown */}
              {job.progress?.contentTypes && Object.keys(job.progress.contentTypes).length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-700">Content Types</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(job.progress.contentTypes).map(([type, data]) => (
                      <div
                        key={type}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          job.progress?.currentContentType === type
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-gray-50'
                        }`}
                      >
                        <span className="capitalize">{type}</span>
                        <span className="font-medium">
                          {data.processed}
                          {job.progress?.currentContentType === type && (
                            <span className="ml-1 text-blue-500">...</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error Message */}
              {job.errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <h3 className="text-sm font-medium text-red-800 mb-1">Error</h3>
                  <p className="text-sm text-red-700">{job.errorMessage}</p>
                </div>
              )}

              {/* Activity Log */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Activity Log
                </h3>
                <div className="bg-gray-900 rounded-lg p-3 font-mono text-xs text-gray-300 max-h-48 overflow-y-auto">
                  {logs.length === 0 ? (
                    <span className="text-gray-500">No activity yet...</span>
                  ) : (
                    logs.map((log, i) => (
                      <div key={i} className="py-0.5">{log}</div>
                    ))
                  )}
                </div>
              </div>

              {/* Job ID */}
              <div className="text-xs text-gray-400 font-mono">
                Job ID: {job.id}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No sync jobs found. Click "Sync" to start one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
